import { Fragment, useEffect, useRef } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Dialog, Transition } from "@headlessui/react";
import { useSelector, useDispatch } from "react-redux";
import {
  deleteReasonStateAction,
  getDeleteIssueState,
  getUpdateIssueState,
  IssueResponse,
  UpdateIssueRequest,
  updateIssueStateAction,
} from "../../../../../redux/features/other-slice";
import { AppDispatch } from "../../../../../redux/store";
import Input from "../../../../../components/input";
import Button from "../../../../../components/button";

interface Props {
  issue: IssueResponse | null;
  setIssue: (value: IssueResponse | null) => void;
  onCompleted: () => void;
}

export default function UpdateIssueDialog(props: Props) {
  const { issue, setIssue, onCompleted } = props;

  const dispatch = useDispatch<AppDispatch>();
  const updateIssueState = useSelector(getUpdateIssueState);
  const deleteIssueState = useSelector(getDeleteIssueState);

  const cancelButtonRef = useRef(null);

  const handleClose = () => {
    setIssue(null);
  };

  const submitUpdateIssue = async (values: UpdateIssueRequest) => {
    if (issue) {
      await dispatch(updateIssueStateAction(values));
    }
  };

  const deleteIssue = async () => {
    if (issue) {
      await dispatch(deleteReasonStateAction({ id: issue.id }));
    }
  };

  useEffect(() => {
    if (updateIssueState.data && updateIssueState.data.success) {
      onCompleted();
      handleClose();
    }
  }, [updateIssueState]);

  useEffect(() => {
    if (deleteIssueState.data && deleteIssueState.data.success) {
      onCompleted();
      handleClose();
    }
  }, [deleteIssueState]);

  return (
    <Transition.Root show={issue !== null} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10 rounded-lg"
        initialFocus={cancelButtonRef}
        onClose={handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="relative transform bg-background-paper text-left shadow-xl transition-all sm:my-4 sm:w-full sm:max-w-lg rounded-lg">
                <div className="p-4">
                  <h3
                    className="text-base font-semibold leading-6"
                    id="modal-title"
                  >
                    Update Issue
                  </h3>
                </div>
                {issue && (
                  <Formik
                    initialValues={
                      {
                        id: issue.id,
                        title: issue.title,
                        description: issue.description,
                      } as UpdateIssueRequest
                    }
                    validationSchema={Yup.object().shape({
                      title: Yup.string().required("Title is required"),
                    })}
                    onSubmit={(values, { setSubmitting }) => {
                      setSubmitting(false);
                      submitUpdateIssue(values);
                    }}
                  >
                    {({
                      values,
                      setValues,
                      errors,
                      handleChange,
                      handleSubmit,
                      dirty,
                    }) => (
                      <Form
                        noValidate
                        autoComplete="off"
                        onSubmit={handleSubmit}
                        className="p-4 space-y-6"
                      >
                        <Input
                          label="Title *"
                          type="text"
                          id="title"
                          value={values.title}
                          onChange={handleChange}
                          errorText={errors.title}
                        />
                        <Input
                          label="Description"
                          type="text"
                          id="description"
                          value={values.description}
                          onChange={handleChange}
                          errorText={errors.description}
                        />
                        {/* <div className="flex flex-col">
                          <label className="text-xs text-text-title">
                            Position
                          </label>
                          <StyledSelect
                            loading={positionsState.loading}
                            options={positionsState.data.map(
                              (pos: PositionResponse) => ({
                                value: pos.id,
                                label: pos.name,
                              })
                            )}
                            value={
                              values.positionId !== null
                                ? (() => {
                                    const index = positionsState.data.findIndex(
                                      (e) => e.id === values.positionId
                                    );
                                    return index !== -1
                                      ? {
                                          value: values.positionId,
                                          label:
                                            positionsState.data[index].name,
                                        }
                                      : {
                                          value: "",
                                          label: "Select...",
                                        };
                                  })()
                                : { value: "", label: "Select..." }
                            }
                            onChange={(
                              value:
                                | SingleValue<OptionValue>
                                | MultiValue<OptionValue>
                            ) => {
                              if (value && isSelectSingleValue(value)) {
                                setValues({
                                  ...values,
                                  positionId: value.value as string,
                                });
                              } else {
                                setValues({
                                  ...values,
                                  positionId: null,
                                });
                              }
                            }}
                          />
                        </div> */}
                        <div className="flex items-center justify-between">
                          <Button
                            label="Delete"
                            type="button"
                            variant="outlined"
                            loading={
                              updateIssueState.loading ||
                              deleteIssueState.loading
                            }
                            onClick={deleteIssue}
                            exClassName="text-error-main border-error-main"
                          />
                          <div className="flex items-center justify-end">
                            <Button
                              label="Cancel"
                              type="button"
                              variant="outlined"
                              loading={
                                updateIssueState.loading ||
                                deleteIssueState.loading
                              }
                              onClick={handleClose}
                              exClassName="mr-2"
                            />
                            <Button
                              label="Save"
                              type="submit"
                              loading={
                                updateIssueState.loading ||
                                deleteIssueState.loading
                              }
                              disabled={
                                Array.isArray(errors) ||
                                Object.values(errors).toString() != "" ||
                                !dirty
                              }
                            />
                          </div>
                        </div>
                      </Form>
                    )}
                  </Formik>
                )}
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
